import z from 'zod';
import dayjs from 'dayjs';

type EnumProps = {
  label: string;
  value: string;
};

export const moduleTabs = [
  { label: 'Summary', value: 'summary' },
  { label: 'RFI', value: 'rfi' },
  { label: 'Submittals', value: 'submittals' },
  { label: 'Change Orders', value: 'change_orders' },
] as const satisfies ReadonlyArray<EnumProps>;

// ---------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------
const isoDateTimeString = z
  .string()
  .optional()
  .nullable()
  .refine((v) => v == null || v === '' || dayjs(v).isValid(), { message: 'Invalid datetime' });

const isoDateString = z
  .string()
  .optional()
  .nullable()
  .refine((v) => v == null || v === '' || dayjs(v, 'YYYY-MM-DD', true).isValid(), {
    message: 'Invalid date',
  });

const money = z.any().optional().nullable(); // backend decimal (string/number) in your system

// ---------------------------------------------------------
// Enums (keep consistent with backend)
// ---------------------------------------------------------
export const rfiStatus = [
  { label: 'Draft', value: 'draft' },
  { label: 'Open', value: 'open' },
  { label: 'Answered', value: 'answered' },
  { label: 'Closed', value: 'closed' },
  { label: 'Void', value: 'void' },
] as const satisfies ReadonlyArray<EnumProps>;

export const rfiPriority = [
  { label: 'Blocking', value: 'blocking' },
  { label: 'High', value: 'high' },
  { label: 'Delays', value: 'delays' },
  { label: 'Normal', value: 'normal' },
  { label: 'Low', value: 'low' },
] as const satisfies ReadonlyArray<EnumProps>;

export const rfiImpact = [
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
  { label: 'None', value: 'none' },
] as const satisfies ReadonlyArray<EnumProps>;

export const submittalStatus = [
  { label: 'Draft', value: 'draft' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Returned', value: 'returned' },
  { label: 'Closed', value: 'closed' },
  { label: 'Void', value: 'void' },
] as const satisfies ReadonlyArray<EnumProps>;

export const submittalType = [
  { label: 'Shop Drawing', value: 'shop_drawing' },
  { label: 'Product Data', value: 'product_data' },
  { label: 'Sample', value: 'sample' },
  { label: 'Test Report', value: 'test_report' },
  { label: 'Calculation', value: 'calculation' },
  { label: 'Warranty', value: 'warranty' },
  { label: 'O&M Manual', value: 'o_and_m' },
  { label: 'Other', value: 'other' },
] as const satisfies ReadonlyArray<EnumProps>;

export const submittalResponse = [
  { label: 'Approved', value: 'approved' },
  { label: 'Approved as Noted', value: 'approved_as_noted' },
  { label: 'Revise and Resubmit', value: 'revise_and_resubmit' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'For Information', value: 'for_information' },
] as const satisfies ReadonlyArray<EnumProps>;

export const changeOrderStatus = [
  { label: 'Draft', value: 'draft' },
  { label: 'ROM', value: 'rom' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Converted', value: 'converted' },
] as const satisfies ReadonlyArray<EnumProps>;

// ---------------------------------------------------------
// Shared list item for “Action table” (Overdue / Due soon)
// ---------------------------------------------------------
export const actionItem = z.object({
  // record type
  type: z.enum(['rfi', 'submittal', 'pending_change']),
  id: z.number(),

  // display fields
  number: z.string(), // RFI-001, SUB-10.1, CP01, etc
  title: z.string(),

  status: z.string(),
  ball_in_court: z.string().nullable().optional(),

  due_at: isoDateTimeString, // normalized (use due_date/due_at on backend)
  is_overdue: z.boolean(),

  // optional context
  priority: z.enum(rfiPriority.map((x) => x.value)).optional().nullable(),
  impact: z.enum(rfiImpact.map((x) => x.value)).optional().nullable(),

  final_response: z.enum(submittalResponse.map((x) => x.value)).optional().nullable(),

  amount: money, // submitted_amount for COs
  age_days: z.number().optional(), // backend computed (fast)
});

// ---------------------------------------------------------
// Dashboard payload
// ---------------------------------------------------------
export const getDashboard = {
  body: z.never(),
  params: z.never(),
  query: z.object({
    project: z.coerce.number({ error: 'Project is required' }),

    // optional filters
    q: z.string().optional(),
    ball_in_court: z.string().optional(),
    only_overdue: z.boolean().optional(),
    due_days: z.coerce.number().optional(), // default 7 in backend
  }),

  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string().optional(),

    data: z.object({
      project_id: z.number(),

      // -----------------
      // KPI cards
      // -----------------
      kpis: z.object({
        rfi: z.object({
          open: z.number(),
          overdue: z.number(),
          blocking: z.number(),
          avg_days_open: z.number().nullable().optional(),
        }),
        submittals: z.object({
          draft: z.number(),
          submitted_waiting: z.number(),
          returned_action: z.number(),
          overdue: z.number(),
          avg_review_days: z.number().nullable().optional(),
        }),
        change_orders: z.object({
          in_review_count: z.number(),
          approved_count: z.number(),
          rejected_count: z.number(),
          total_requested: money,
          total_approved: money,
          total_rejected: money,
          total_in_review: money,
        }),
      }),

      // -----------------
      // Donut / status buckets
      // -----------------
      change_orders_by_status: z.array(
        z.object({
          status: z.enum(changeOrderStatus.map((x) => x.value)),
          count: z.number(),
          amount: money,
        }),
      ),

      // -----------------
      // Action tables
      // -----------------
      overdue: z.array(actionItem),
      due_soon: z.array(actionItem),

      // -----------------
      // Mini “top lists”
      // -----------------
      top_rfis: z.array(
        z.object({
          id: z.number(),
          rfi_number: z.string(),
          subject: z.string(),
          status: z.enum(rfiStatus.map((x) => x.value)),
          priority: z.enum(rfiPriority.map((x) => x.value)),
          impact: z.enum(rfiImpact.map((x) => x.value)),
          due_date: isoDateTimeString,
          ball_in_court: z.string().nullable().optional(),
          is_overdue: z.boolean(),
        }),
      ),

      top_submittals: z.array(
        z.object({
          id: z.number(),
          number: z.string(),
          revision: z.number(),
          title: z.string(),
          status: z.enum(submittalStatus.map((x) => x.value)),
          submittal_type: z.enum(submittalType.map((x) => x.value)),
          spec_section: z.string().nullable().optional(),
          vendor: z.string().nullable().optional(),
          final_response: z.enum(submittalResponse.map((x) => x.value)).nullable().optional(),
          due_at: isoDateTimeString,
          ball_in_court: z.string().nullable().optional(),
          is_overdue: z.boolean(),
        }),
      ),

      top_change_orders: z.array(
        z.object({
          id: z.number(),
          cp_number: z.string(),
          title: z.string(),
          status: z.enum(changeOrderStatus.map((x) => x.value)),
          due_at: isoDateTimeString,
          ball_in_court: z.string().nullable().optional(),
          submitted_amount: money,
          approved_amount: money,
          is_overdue: z.boolean(),
        }),
      ),

      // -----------------
      // Ball in court breakdown (fast usefulness)
      // -----------------
      ball_in_court: z.object({
        rfi: z.array(z.object({ name: z.string(), count: z.number() })),
        submittals: z.array(z.object({ name: z.string(), count: z.number() })),
        change_orders: z.array(z.object({ name: z.string(), count: z.number() })),
      }),

      // -----------------
      // Optional: recent activity + attachments (later)
      // -----------------
      recent_activity: z
        .array(
          z.object({
            type: z.enum(['rfi', 'submittal', 'pending_change']),
            id: z.number(),
            number: z.string(),
            title: z.string(),
            activity_type: z.string(),
            user_name: z.string().nullable().optional(),
            description: z.string().nullable().optional(),
            created_at: isoDateTimeString,
          }),
        )
        .optional(),

      recent_attachments: z
        .array(
          z.object({
            type: z.enum(['rfi', 'submittal', 'pending_change']),
            id: z.number(),
            number: z.string(),
            filename: z.string(),
            file_url: z.string().nullable().optional(),
            uploaded_by_name: z.string().nullable().optional(),
            created_at: isoDateTimeString,
          }),
        )
        .optional(),
    }),

    errors: z.any().optional(),
  }),
};

const getDashboardSchema = z.object(getDashboard);
export type GetDashboard = z.infer<typeof getDashboardSchema>;
